import { Controller } from '@hotwired/stimulus'
import { TomOption } from 'tom-select/src/types/core.js'
import { TomSelectElement } from '../../types/common'

type FieldAddedEvent = CustomEvent<{ node: HTMLElement }>

// Manages the per-field "extensions" UI in the submission-view form builder:
// the extension selector, the nested rule selects, and adding/removing/toggling
// nested extension fields. Runs alongside SubmissionViewFieldsController on the
// same element; that controller dispatches `fieldAdded` when it clones a new
// top-level field so we can activate the field's (deferred) extension selector.
export default class SubmissionViewExtensionFieldsController extends Controller {
  declare readonly extensionFieldPlaceholderTarget: HTMLElement
  declare readonly hasExtensionFieldPlaceholderTarget: boolean

  static targets = ['extensionFieldPlaceholder']

  add = this.#handleAdd.bind(this)
  toggle = this.#handleToggle.bind(this)
  remove = this.#handleRemove.bind(this)
  onFieldAdded = this.#handleFieldAdded.bind(this)

  connect() {
    // Wire any server-rendered extension selectors
    // Timeout to ensure the DOM is fully rendered
    setTimeout(() => {
      this.element
        .querySelectorAll<TomSelectElement>('.extension-selector[data-controller~="tom-select"]')
        .forEach(select => {
          this.#wireExtensionSelector(select)
        })
    }) 
  }

  // Show/hide a field's extension selector + list when its checkbox toggles.
  // When unchecked, the extension fieldsets are disabled so their inputs are
  // excluded from the submitted form — removing extensions from this parent's
  // payload on create/update. Re-checking restores them.
  #handleToggle(event: Event) {
    const checkbox = event.target as HTMLInputElement
    const container = checkbox
      .closest<HTMLElement>('.submission-field-container')
      ?.querySelector<HTMLElement>('.extensions-container')
    if (!container) return

    const enabled = checkbox.checked
    container.classList.toggle('hidden', !enabled)
    container.querySelectorAll<HTMLFieldSetElement>('.extension-field').forEach(field => {
      field.disabled = !enabled
    })
  }

  // Clone the extension template and nest it under the parent field, producing
  // IMPORTANT: names submission_view[payload][PARENT][extensions][CHILD][KEY].
  #handleAdd(event: Event) {
    if (!this.hasExtensionFieldPlaceholderTarget) return

    const select = event.target as TomSelectElement
    const option = select.selectedOptions[0]
    if (!option || !option.value) return

    const parent = select.closest<HTMLElement>('.submission-field-container')
    const list = parent?.querySelector<HTMLElement>('.extensions-list')
    const parentName = parent?.dataset.fieldName
    if (!list || !parentName) return

    const node = this.extensionFieldPlaceholderTarget.cloneNode(true) as HTMLFieldSetElement
    this.#configureExtensionNode(node, parentName, {
      name: option.value,
      type: option.dataset.inputType || '',
      label: option.dataset.inputLabel || '',
    })

    node.querySelectorAll('input[name*=extension_placeholder]').forEach(child => {
      child.remove()
    })
    list.append(node)

    select.tomselect?.clear()
  }

  // Remove a single nested extension field. The parent's selector re-enables the
  // freed option on its next open (recomputed in #disableUsedExtensions).
  #handleRemove(event: Event) {
    const target = event.target as HTMLElement
    target.closest('.extension-field')?.remove()
  }

  // Activate the (deferred) extension selector on a newly cloned top-level field.
  #handleFieldAdded(event: FieldAddedEvent) {
    const node = event.detail?.node
    if (node) this.#activateExtensionSelector(node)
  }

  #configureExtensionNode(
    node: HTMLFieldSetElement,
    parentName: string,
    child: { name: string; type: string; label: string },
  ) {
    const prefix = `submission_view[payload][${parentName}][extensions][${child.name}]`

    delete node.dataset.submissionViewExtensionFieldsTarget
    node.classList.remove('hidden', 'mock-field')
    node.disabled = false
    node.dataset.fieldName = child.name

    const title = node.querySelector('.title')
    if (title) title.innerHTML = child.label

    const typeInput = node.querySelector<HTMLInputElement>('input[name$="[type]"]')
    if (typeInput) {
      typeInput.name = `${prefix}[type]`
      typeInput.value = child.type
    }

    const labelInput = node.querySelector<HTMLInputElement>('input[name$="[label]"]')
    if (labelInput) {
      labelInput.name = `${prefix}[label]`
      labelInput.value = child.label
    }

    const textarea = node.querySelector<HTMLTextAreaElement>('textarea')
    if (textarea) {
      textarea.name = `${prefix}[subtitle]`
      textarea.value = ''
    }

    const hideBox = node.querySelector<HTMLInputElement>('.hide-label-check-box')
    if (hideBox) {
      hideBox.name = `${prefix}[hide_label]`
      const hidden = hideBox.previousSibling as HTMLInputElement | null
      if (hidden && hidden.tagName === 'INPUT') hidden.name = `${prefix}[hide_label]`
      hideBox.checked = false
    }

    // Rule selects are tom-select (deferred on the mock template). Rename them
    // and activate tom-select; Stimulus initializes them once the node is
    // appended to the DOM.
    this.#prepareRuleSelect(node, '.rule-trigger', `${prefix}[rules][trigger]`)
    this.#prepareRuleSelect(node, '.rule-action', `${prefix}[rules][action][]`)
  }

  #prepareRuleSelect(node: HTMLElement, selector: string, name: string) {
    const select = node.querySelector<HTMLSelectElement>(selector)
    if (!select) return

    select.name = name
    if (!select.dataset.controller) select.dataset.controller = 'tom-select'
  }

  // tom-select is deferred on cloned templates (it rewrites the DOM on init).
  // Activate it once the parent field is in the DOM and wire its filtering.
  #activateExtensionSelector(node: HTMLElement) {
    const selector = node.querySelector<TomSelectElement>('.extension-selector')
    if (!selector || selector.dataset.controller) return

    selector.dataset.controller = 'tom-select'
    setTimeout(() => this.#wireExtensionSelector(selector))
  }

  #wireExtensionSelector(select: TomSelectElement) {
    if (!select?.tomselect || select.dataset.extensionWired) return

    select.dataset.extensionWired = '1'
    select.tomselect.on('dropdown_open', () => this.#disableUsedExtensions(select))
  }

  // Prevent adding the same field twice to one parent. Recomputed on each open,
  // so removing an extension re-enables its option.
  #disableUsedExtensions(select: TomSelectElement) {
    const parent = select.closest<HTMLElement>('.submission-field-container')
    const used = new Set<string>()
    parent?.querySelectorAll<HTMLElement>('.extension-field').forEach(el => {
      if (el.dataset.fieldName) used.add(el.dataset.fieldName)
    })

    Object.values(select.tomselect.options).forEach(option => {
      this.#toggleOption(option, false)
    })
    used.forEach(name => {
      const option = select.tomselect.options[name]
      if (option) this.#toggleOption(option, true)
    })
  }

  #toggleOption(option: TomOption, value: boolean) {
    if (typeof option.$div === 'undefined') return

    option.disabled = value
    option.$div.ariaDisabled = value

    if (value) {
      option.$div.removeAttribute('data-selectable')
    } else {
      option.$div.setAttribute('data-selectable', true)
    }
  }
}
